import { useState, useEffect } from 'react';
import { Card, Tabs, Table, Button, message, Modal, Form, Input, InputNumber, Switch, Tree, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const h = () => ({ Authorization: 'Bearer ' + token() });

export default function PermissionManagement() {
  const [menus, setMenus] = useState([]);
  const [flatMenus, setFlatMenus] = useState([]);
  const [roles, setRoles] = useState([{ id: 1, name: '经销商' }, { id: 2, name: '厂家' }, { id: 3, name: '管理员' }, { id: 4, name: '商务人员' }]);
  const [roleMenus, setRoleMenus] = useState({});  // role_id -> Set<menu_id>
  const [menuModal, setMenuModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm] = Form.useForm();

  // Fetch all menus flat list (for admin CRUD)
  const fetchMenus = async () => {
    try {
      const r = await axios.get(API + '/admin/menus', { headers: h() });
      if (r.data.success) {
        setFlatMenus(r.data.menus || []);
      }
    } catch (e) {
      message.error('加载菜单失败');
    }
  };

  // Fetch user menus (tree)
  const fetchUserMenus = async () => {
    try {
      const r = await axios.get(API + '/menus', { headers: h() });
      if (r.data.success) {
        const tree = r.data.menus || [];
        setMenus(tree);
      }
    } catch (e) {
      message.error('加载菜单树失败');
    }
  };

  // Fetch role permissions
  const fetchRoleMenus = async (roleId) => {
    try {
      const r = await axios.get(API + '/admin/role-menus/' + roleId, { headers: h() });
      if (r.data.success) {
        setRoleMenus(prev => ({ ...prev, [roleId]: new Set(r.data.menu_ids || []) }));
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchMenus();
    fetchUserMenus();
    roles.forEach(r => fetchRoleMenus(r.id));
  }, []);

  // Build tree data for Ant Design Tree (uses flat list)
  const buildTreeData = (items) => {
    const map = {};
    const roots = [];
    items.forEach(item => {
      map[item.id] = { ...item, title: item.name || item.menu_name, key: item.id, children: [] };
    });
    items.forEach(item => {
      if (item.parent_id && map[item.parent_id]) {
        map[item.parent_id].children.push(map[item.id]);
      } else if (!item.parent_id || item.parent_id === 0) {
        roots.push(map[item.id]);
      }
    });
    return roots;
  };

  const handleSaveRole = async (roleId) => {
    const ids = [...(roleMenus[roleId] || new Set())];
    try {
      await axios.put(API + '/admin/role-menus/' + roleId, { menu_ids: ids }, { headers: h() });
      message.success('权限保存成功');
    } catch { message.error('保存失败'); }
  };

  const handleMenuSave = async () => {
    const vals = await menuForm.validateFields();
    try {
      if (editingMenu) {
        await axios.put(API + '/admin/menus/' + editingMenu.id, vals, { headers: h() });
      } else {
        await axios.post(API + '/admin/menus', vals, { headers: h() });
      }
      message.success(editingMenu ? '更新成功' : '创建成功');
      setMenuModal(false);
      fetchMenus();
      fetchUserMenus();
    } catch (e) {
      if (e?.response) message.error(e.response.data?.detail || '操作失败');
    }
  };

  const handleMenuDelete = async (id) => {
    try {
      await axios.delete(API + '/admin/menus/' + id, { headers: h() });
      message.success('删除成功');
      fetchMenus();
      fetchUserMenus();
    } catch { message.error('删除失败'); }
  };

  const openEdit = (m) => {
    setEditingMenu(m);
    menuForm.setFieldsValue({
      parent_id: m.parent_id || 0,
      name: m.name || m.menu_name,
      path: m.path || '',
      icon: m.icon || '',
      sort_order: m.sort_order || 0,
      visible: m.visible !== false,
    });
    setMenuModal(true);
  };

  const openCreate = () => {
    setEditingMenu(null);
    menuForm.resetFields();
    menuForm.setFieldsValue({ parent_id: 0, visible: true, sort_order: 0, path: '', icon: '' });
    setMenuModal(true);
  };

  const menuColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '菜单名称', dataIndex: 'name', render: (v, r) => r.menu_name || v },
    { title: '路径', dataIndex: 'path' },
    { title: '图标', dataIndex: 'icon' },
    { title: '排序', dataIndex: 'sort_order' },
    {
      title: '操作', width: 160, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleMenuDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const treeData = buildTreeData(flatMenus);

  return (
    <Card title="权限管理">
      <Tabs items={[
        {
          key: 'menu', label: '菜单管理', children: <>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ marginBottom: 12 }}>
              新增菜单
            </Button>
            <Table dataSource={flatMenus} rowKey="id" columns={menuColumns} size="small" />
          </>,
        },
        {
          key: 'role', label: '角色权限', children: <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {roles.map(r => (
              <Card key={r.id} size="small" title={r.name} style={{ flex: '1 1 280px', minWidth: 280 }}
                extra={<Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => handleSaveRole(r.id)}>保存</Button>}>
                <Tree
                  checkable
                  defaultExpandAll
                  treeData={treeData}
                  checkedKeys={[...(roleMenus[r.id] || new Set())]}
                  onCheck={(checked) => {
                    if (Array.isArray(checked)) {
                      setRoleMenus(prev => ({ ...prev, [r.id]: new Set(checked) }));
                    } else {
                      setRoleMenus(prev => ({ ...prev, [r.id]: new Set(checked.checked) }));
                    }
                  }}
                />
              </Card>
            ))}
          </div>,
        },
      ]} />

      <Modal
        title={editingMenu ? '编辑菜单' : '新增菜单'}
        open={menuModal}
        onOk={handleMenuSave}
        onCancel={() => setMenuModal(false)}
        destroyOnClose
      >
        <Form form={menuForm} layout="vertical">
          <Form.Item name="parent_id" label="父级菜单ID">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="name" label="菜单名称" rules={[{ required: true, message: '请输入菜单名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="path" label="路径">
            <Input />
          </Form.Item>
          <Form.Item name="icon" label="图标">
            <Input />
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="visible" label="可见" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
